import { Column, Index, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { ClientAddressEntity } from './client-address.entity';
import { ClientEntity } from './client.entity';
import { CompanyEntity } from './company.entity';
import { InvoiceEntity } from './invoice.entity';
import { MPaymentPaymentMethodEntity } from './m-payment-payment-method.entity';
import { PaymentScheduleOccurrenceEntity } from './payment-schedule-occurrence.entity';

/**
 * Schedule type discriminator:
 * - autopay: Auto-charge when invoices are due (matches by billing address)
 * - recurring: Fixed-amount charges on a schedule (subscription/retainer)
 * - installment: Split an invoice balance into N scheduled payments
 */
export type PaymentScheduleType = 'autopay' | 'recurring' | 'installment';

/**
 * Schedule lifecycle status:
 * - active: Schedule is running and will process payments
 * - paused: Temporarily stopped; no new occurrences will be created or processed
 * - completed: All planned payments finished (e.g., all installments paid)
 * - cancelled: Permanently stopped by user action
 * - failed: Stopped due to repeated failures (e.g., payment method invalid)
 */
export type PaymentScheduleStatus =
	| 'active'
	| 'paused'
	| 'completed'
	| 'cancelled'
	| 'failed';

/**
 * Payment frequency options:
 * - on_due_date: Only valid for autopay — processes when invoice due date arrives
 * - weekly/biweekly/monthly/quarterly/annually: Standard recurring frequencies
 */
export type PaymentScheduleFrequency =
	| 'weekly'
	| 'biweekly'
	| 'monthly'
	| 'quarterly'
	| 'annually'
	| 'on_due_date';

@Index('payment_schedule_status_next_run_date', ['status', 'nextRunDate'], {})
@Index('payment_schedule_fk_client__id', ['fkClientId'], {})
@Index('payment_schedule_fk_invoice__id', ['fkInvoiceId'], {})
@Index('payment_schedule_fk_payment_method__id', ['fkPaymentMethodId'], {})
@Index(
	'payment_schedule_fk_client_address__id_billing',
	['fkClientAddressIdBilling'],
	{},
)
@Entity('payment_schedule', {}, { label: 'Payment Schedule' })
export class PaymentScheduleEntity extends BaseEntity {
	@Column('varchar', {
		name: 'type',
		length: 32,
		comment: 'Schedule type: autopay, recurring, or installment',
	})
	public type!: PaymentScheduleType;

	@Column('varchar', {
		name: 'status',
		length: 32,
		default: () => "'active'",
		comment: 'Current lifecycle status of the schedule',
	})
	public status!: PaymentScheduleStatus;

	@Column('int', { name: '_fk_client__id', unsigned: true })
	public fkClientId!: number;

	@Column('int', { name: '_fk_company__id', unsigned: true })
	public fkCompanyId!: number;

	@Column('int', {
		name: '_fk_payment_method__id',
		unsigned: true,
		comment: 'Must be a gateway-backed method (Credit Card or ACH)',
	})
	public fkPaymentMethodId!: number;

	@Column('int', {
		name: '_fk_client_address__id_billing',
		unsigned: true,
		comment:
			'Billing address. For autopay, only invoices matching this address are processed',
	})
	public fkClientAddressIdBilling!: number;

	@Column('int', {
		name: '_fk_invoice__id',
		nullable: true,
		unsigned: true,
		comment: 'Required for installment type; null for autopay and recurring',
	})
	public fkInvoiceId!: number | null;

	@Column('varchar', {
		name: 'name',
		length: 255,
		comment: 'User-provided label for this schedule',
	})
	public name!: string;

	@Column('text', {
		name: 'description',
		nullable: true,
		comment: 'Optional notes about this schedule',
	})
	public description!: string | null;

	@Column('varchar', {
		name: 'frequency',
		length: 32,
		comment: 'Payment frequency. on_due_date is only valid for autopay',
	})
	public frequency!: PaymentScheduleFrequency;

	@Column('smallint', {
		name: 'dayOfMonth',
		nullable: true,
		comment: '1-28. Used for monthly/quarterly/annually frequencies',
	})
	public dayOfMonth!: number | null;

	@Column('smallint', {
		name: 'dayOfWeek',
		nullable: true,
		comment: '0-6 (Sun-Sat). Used for weekly/biweekly frequencies',
	})
	public dayOfWeek!: number | null;

	@Column('decimal', {
		name: 'fixedAmount',
		nullable: true,
		precision: 13,
		scale: 2,
		comment:
			'Required for recurring. For installments, calculated as total / numberOfInstallments. Null for autopay',
	})
	public fixedAmount!: string | null;

	@Column('smallint', {
		name: 'numberOfInstallments',
		nullable: true,
		comment: 'Only used for installment type',
	})
	public numberOfInstallments!: number | null;

	@Column('date', {
		name: 'startDate',
		comment: 'When the schedule becomes active',
	})
	public startDate!: string;

	@Column('date', {
		name: 'endDate',
		nullable: true,
		comment:
			'Optional end date. Auto-calculated for installments. Null = indefinite for recurring',
	})
	public endDate!: string | null;

	@Column('datetime', {
		name: 'nextRunDate',
		nullable: true,
		comment:
			'Denormalized pre-computed next execution date for efficient scheduler queries',
	})
	public nextRunDate!: Date | null;

	@Column('datetime', {
		name: 'lastRunDate',
		nullable: true,
		comment: 'Timestamp of last successful payment execution',
	})
	public lastRunDate!: Date | null;

	@Column('smallint', {
		name: 'maxRetries',
		default: 3,
		comment: 'Maximum retry attempts per occurrence',
	})
	public maxRetries!: number;

	@Column('smallint', {
		name: 'retryDelayHours',
		default: 24,
		comment: 'Hours to wait between retry attempts',
	})
	public retryDelayHours!: number;

	@Column('decimal', {
		name: 'totalAmountProcessed',
		precision: 13,
		scale: 2,
		default: () => "'0.00'",
		comment: 'Running total of all successfully processed payments',
	})
	public totalAmountProcessed!: string;

	@Column('int', {
		name: 'completedOccurrences',
		default: 0,
		comment: 'Count of successfully completed payment occurrences',
	})
	public completedOccurrences!: number;

	@Column('int', {
		name: 'failedOccurrences',
		default: 0,
		comment: 'Count of permanently failed payment occurrences',
	})
	public failedOccurrences!: number;

	// --- Relationships ---

	@ManyToOne(() => ClientEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: '_fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity;

	@ManyToOne(() => CompanyEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: '_fk_company__id', referencedColumnName: 'id' }])
	public fkCompany!: CompanyEntity;

	@ManyToOne(() => MPaymentPaymentMethodEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: '_fk_payment_method__id', referencedColumnName: 'id' }])
	public fkPaymentMethod!: MPaymentPaymentMethodEntity;

	@ManyToOne(() => ClientAddressEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([
		{ name: '_fk_client_address__id_billing', referencedColumnName: 'id' },
	])
	public fkClientAddressIdBilling2!: ClientAddressEntity;

	@ManyToOne(() => InvoiceEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: '_fk_invoice__id', referencedColumnName: 'id' }])
	public fkInvoice!: InvoiceEntity;

	@OneToMany(
		() => PaymentScheduleOccurrenceEntity,
		(occurrence) => occurrence.fkPaymentSchedule,
	)
	public occurrences!: Array<PaymentScheduleOccurrenceEntity>;
}
